import dayjs from 'dayjs';

import { PrintLabelEntityTypes } from '../@types/print-label.type';
import { getPartById } from '../api/part';
import { getSignedMessage } from '../api/printLabel';
import { DEFAULT_PRINTER_NAME, REACT_APP_QZ_CERTIFICATE } from '../configs/envs';

const FALLBACK_PRINTER_NAME = 'Zebra150';

// Some DB edits land with the ZPL wrapped to ~70 columns, which can (a) split
// `{{placeholder}}` tokens across newlines and (b) leave literal spaces
// inside placeholder tokens when the wrap lands mid-word — `{{part
// TypeCategory}}` instead of `{{partTypeCategory}}`. Both break every
// substitution regex (BFF's and ours). This normaliser heals both quirks
// before substitution runs:
//   1. strip CR/LF anywhere in the ZPL
//   2. strip whitespace *inside* `{{...}}` tokens only (preserves other ZPL
//      whitespace)
const normaliseLabelText = (s: string): string => {
  const dewrapped = s.replace(/\r?\n/g, '');
  return dewrapped.replace(
    /{{([^}]*)}}/g,
    (_match, key: string) => `{{${key.replace(/\s+/g, '')}}}`
  );
};

// Maps a partType.categoryCode to the short uppercase label the printed
// sticker uses. Unknown codes fall through to PART so we never print empty.
const mapCategoryToLabel = (code: string | undefined | null): string => {
  if (!code) return 'PART';
  const normalized = code.toLowerCase();
  if (normalized === 'serialised' || normalized === 'serialized') return 'SERIAL';
  if (normalized === 'kit') return 'KIT';
  return 'PART';
};

// Emits the ZPL for one SW/HW/FW/F-T label+value pair, or an empty string
// when the value is missing so the printed label stays blank rather than
// showing a labelled-empty row. Called from the B1 workaround to build
// the `{{swBlock}}` / `{{hwBlock}}` / `{{fwBlock}}` / `{{ftBlock}}`
// placeholders. Coords and fonts match the existing 20pt pair style used
// elsewhere in the template.
const buildPairBlock = (
  label: string,
  value: string,
  labelX: number,
  valueX: number,
  y: number
): string =>
  value
    ? `^FT${labelX},${y}^A0I,20,20^FH\\^FD${label}:^FS` +
      `^FT${valueX},${y}^A@I,20,20,TT0003M_^FH\\^CI17^F8^FD${value}^FS^CI0`
    : '';

// TEMP B1 workaround — the BFF substitutes `{{<key>}}` for every top-level
// key on the part object, which: (a) only hits the first occurrence of each
// placeholder (so a template with `{{serial1}}` twice prints the second one
// literally), (b) skips any key whose value is empty/null/undefined (so
// `{{serial2}}` prints literally for parts with no second serial), and (c)
// stringifies nested objects (like `part.partCondition` → `[object Object]`).
// We fetch `/parts/{id}` and do a second-pass substitution using placeholder
// names chosen to NOT collide with any top-level part field (so the BFF
// leaves them alone):
//   `{{partTypeName}}`, `{{partTypeNumber}}`, `{{partTypeCategory}}`,
//   `{{partConditionName}}` — note the `Name` suffix on the last one to
//   avoid colliding with the `partCondition` object.
// `serial1`/`serial2` ARE top-level keys, so we re-substitute them here too
// to cover the BFF's empty-value skip and single-replace quirks.
// Remove this helper + its tests once the BFF is fixed (tracked in Backlog.md).
// Complexity is naturally high due to per-field `?? ''` defaulting across
// ~10 fields — the helper is a flat blob of substitutions on purpose, not
// a logic-heavy function, so the complexity rule isn't catching a real risk.
// eslint-disable-next-line complexity
const applyPartWorkaroundSubstitutions = async (
  labelText: string,
  partId: number
): Promise<string> => {
  try {
    const part = await getPartById(partId);
    // The live API response includes `partCondition: { name, code, … }` but
    // the `PartObject` interface still calls it `condition` / `conditionName`
    // from an older schema. Fall back gracefully across both shapes.
    const partConditionName =
      (part as { partCondition?: { name?: string | null } | null }).partCondition?.name ??
      part.conditionName ??
      '';
    return replaceDynamicValuesInTemplate(
      {
        serial1: part.serial1 ?? '',
        serial2: part.serial2 ?? '',
        partTypeName: part.partType?.name ?? '',
        partTypeNumber: part.partType?.number ?? '',
        partTypeCategory: mapCategoryToLabel(part.partType?.categoryCode),
        partConditionName,
        // 2×2 grid of optional fields — empty values render as blank rows
        // rather than orphan labels. Coords kept in sync with the ZPL:
        // SW / HW on Serial 2 row (y=100), FW / F-T on Status row (y=50).
        // Both pairs shifted ~50 dots reading-right (lower raster x) on
        // 2026-04-21 to pull them away from the big Serial 2 / Status
        // values and give them more breathing room.
        swBlock: buildPairBlock('SW', part.softwareVersion ?? '', 400, 340, 100),
        hwBlock: buildPairBlock('HW', part.hardwareRevision ?? '', 210, 150, 100),
        fwBlock: buildPairBlock('FW', part.firmwareVersion ?? '', 400, 340, 50),
        ftBlock: buildPairBlock('F/T', part.fleetTagName ?? '', 210, 150, 50),
      },
      labelText
    );
  } catch {
    // If the fetch fails (auth, network, etc) we'd rather print a partially
    // substituted label than no label at all. The BFF already filled in
    // most placeholders (currentDateTime, partType.name, etc.); any
    // unsubstituted `{{...}}` tokens stay visible as the bug they are.
    return labelText;
  }
};

// Replaces `{{key}}` placeholders in `template` with matching values from
// `data`. Placeholders whose key is NOT in `data` are left intact so a later
// substitution pass (or a visible `{{foo}}` on the final output) can surface
// them — silently stripping them masks real bugs, as happened with
// currentDateTime when running after the B1 part workaround.
export const replaceDynamicValuesInTemplate = (
  data: Record<string, string | number | undefined | null>,
  template: string
) => {
  if (!template) return '';

  const placeholders = template.match(/{{(.*?)}}/g);
  if (!placeholders) return template;

  // Dedupe — if the same placeholder appears N times, one replaceAll handles
  // all of them in a single pass instead of iterating N times.
  const uniquePlaceholders = new Set(placeholders);
  uniquePlaceholders.forEach((raw) => {
    const key = raw.replace(/{|}/g, '');
    if (!(key in data)) return;
    const value = data[key] ?? '';
    template = template.replaceAll(raw, String(value));
  });

  return template;
};

// Dynamic-import qz-tray only when a print actually fires so the ~140 KB
// client doesn't ship in main.js for every page that never prints.
const sendToQzPrinter = (printerName: string, labelText: string): Promise<unknown> =>
  import('qz-tray').then(({ default: qz }) => {
    return qz.websocket
      .connect()
      .then(() => qz.printers.find())
      .then((printers: string[] | string | undefined) => {
        const printerList = Array.isArray(printers) ? printers : [];
        if (
          !printerList.length ||
          !printerList.some(
            (printer: string) => printer.toLocaleLowerCase() === printerName.toLocaleLowerCase()
          )
        ) {
          alert(
            `Printer "${printerName}" not found. Check that the printer is powered on and visible in QZ Tray.`
          );
          return Promise.resolve();
        }

        const config = qz.configs.create(printerName);
        const textToPrint = replaceDynamicValuesInTemplate(
          { currentDateTime: dayjs().format('YYYY-MM-DD HH:mm') },
          labelText
        );
        return qz.print(config, [textToPrint]);
      })
      .then(() => qz.websocket.disconnect())
      .catch(async (e: unknown) => {
        // Still tear the websocket down, but surface the original error to
        // the caller. If disconnect itself fails, the print error is more
        // useful than the teardown failure, so swallow the disconnect one.
        try {
          await qz.websocket.disconnect();
        } catch {
          /* ignored — prioritise the original print error */
        }
        throw e;
      });
  });

export const printLabel = (labelText: string, entityType?: string, entityId?: number) => {
  if (!labelText) {
    alert('No label data was returned for this item. Please try again or contact support.');
    return Promise.resolve();
  }

  if (entityType === PrintLabelEntityTypes.FreightOrderLabel) {
    const byteCharacters = atob(labelText);
    const byteNumbers = new Array(byteCharacters.length)
      .fill(0)
      .map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);

    window.open(blobUrl, '_blank');
    // Let the new tab load the blob, then reclaim the object URL. 500ms is
    // enough for the browser to latch onto the resource without holding the
    // Blob in memory for the life of the tab.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 500);
    return Promise.resolve();
  }

  const printerName = DEFAULT_PRINTER_NAME || FALLBACK_PRINTER_NAME;

  // Heal the ZPL before any substitution runs so that placeholder tokens
  // corrupted by SQL-client paste-wrap (newlines and/or stray inner
  // whitespace — e.g. `{{part TypeCategory}}`) still match.
  const normalisedLabelText = normaliseLabelText(labelText);

  // Front-load the B1 workaround substitution (no-op for non-Part entity
  // types or when the caller didn't pass an id) so the rest of the pipeline
  // can treat the label text as ready-to-print.
  const resolvedLabelPromise =
    entityType === PrintLabelEntityTypes.Part && entityId
      ? applyPartWorkaroundSubstitutions(normalisedLabelText, entityId)
      : Promise.resolve(normalisedLabelText);

  return resolvedLabelPromise.then((resolvedLabel) => sendToQzPrinter(printerName, resolvedLabel));
};

export const initializeQZTrayCertificate = async () => {
  // Fire-and-forget from App.tsx on mount. qz-tray loads in the background off
  // the critical path, and the cert/signature callbacks register before any
  // real print attempt because printLabel also dynamically imports qz-tray.
  const { default: qz } = await import('qz-tray');

  qz.security.setCertificatePromise((resolve: (value: string) => void) => {
    resolve(REACT_APP_QZ_CERTIFICATE);
  });

  qz.security.setSignatureAlgorithm('SHA512');
  qz.security.setSignaturePromise((toSign: string) => {
    return (resolve: (value: string) => void, reject: () => void) => {
      getSignedMessage(toSign)
        .then((signature) => {
          resolve(signature);
        })
        .catch(() => reject());
    };
  });
};
